import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Trash2, RefreshCw, Pencil, Eye, CalendarClock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ScheduleDialog, fmtDublin } from "@/components/ScheduleDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, StageBadge } from "@/components/Layout";
import { api, errMsg, fullName, ago } from "@/lib/api";

const report = (d, verb) => {
  const parts = [`${d.sent || d.skipped} ${verb}`];
  if (d.cancelled) parts.push(`${d.cancelled} cancelled (lead replied/closed or left campaign)`);
  if (d.failed) parts.push(`${d.failed} failed${d.errors?.[0] ? `: ${d.errors[0]}` : ""}`);
  toast[d.failed ? "error" : "success"](parts.join(" · "));
};

function OutboxDialog({ id, onClose, onDone }) {
  const [item, setItem] = useState(null);
  const [edit, setEdit] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [cta, setCta] = useState("");
  const [busy, setBusy] = useState(false);
  const [sched, setSched] = useState(false);
  useEffect(() => {
    if (!id) return;
    setItem(null); setEdit(false);
    api.get(`/outbox/${id}`).then((r) => { setItem(r.data); setSubject(r.data.subject); setBody(r.data.body); setCta(r.data.cta_text || "Book your free consultation"); }).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  const save = async () => {
    try { const { data } = await api.put(`/outbox/${id}`, { subject, body, cta_text: cta }); setItem(data); setEdit(false); toast.success("Email updated"); onDone(true); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (kind) => {
    setBusy(true);
    try { const { data } = await api.post(`/outbox/${kind}`, { ids: [id] }); report(data, kind === "send" ? "sent" : "removed"); if (!data.failed) { onClose(); onDone(true); } }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="outbox-dialog">
        <DialogHeader><DialogTitle>{item ? `${item.lead?.company_name} · ${fullName(item.lead)} <${item.lead?.email}>` : "Loading…"}</DialogTitle></DialogHeader>
        {item && (edit ? (
          <div className="space-y-3">
            <div><div className="label-xs mb-1">Subject</div><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outbox-edit-subject" /></div>
            <div><div className="label-xs mb-1">Button text</div><Input value={cta} onChange={(e) => setCta(e.target.value)} data-testid="outbox-edit-cta" /></div>
            <div><div className="label-xs mb-1">Body (merge fields like {"{{first_name}}"} still work)</div><Textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-xs" data-testid="outbox-edit-body" /></div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm"><span className="label-xs mr-2">Subject</span><span className="font-semibold" data-testid="outbox-preview-subject">{item.preview_subject}</span></div>
            <iframe title="preview" sandbox="" srcDoc={item.preview_html} className="w-full h-[420px] border rounded-lg bg-white" data-testid="outbox-preview-frame" />
            {item.scheduled_at && <div className="text-xs text-indigo-700" data-testid="outbox-item-scheduled">Scheduled for {fmtDublin(item.scheduled_at)} (Irish time)</div>}
            {item.error && <div className="text-xs text-red-600" data-testid="outbox-item-error">Last attempt failed: {item.error}</div>}
          </div>
        ))}
        {item && item.status !== "queued" && <div className="text-xs text-slate-500 pt-2" data-testid="outbox-item-status">Status: {item.status === "skipped" ? "removed" : item.status}</div>}
        {item?.status === "queued" && (
          <div className="flex flex-wrap gap-2 justify-end pt-2">
            {edit ? <><Button variant="ghost" onClick={() => setEdit(false)} data-testid="outbox-edit-cancel">Cancel</Button><Button onClick={save} data-testid="outbox-edit-save">Save changes</Button></>
              : <>
                <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" disabled={busy} onClick={() => act("skip")} data-testid="outbox-remove-one"><Trash2 size={14} className="mr-1" />Remove</Button>
                <Button variant="outline" onClick={() => setSched(true)} data-testid="outbox-schedule-one"><CalendarClock size={14} className="mr-1" />Schedule</Button>
                <Button variant="outline" onClick={() => setEdit(true)} data-testid="outbox-edit-btn"><Pencil size={14} className="mr-1" />Edit</Button>
                <Button disabled={busy} onClick={() => act("send")} data-testid="outbox-send-one"><Send size={14} className="mr-1" />Send now</Button>
              </>}
          </div>
        )}
        {item && <ScheduleDialog ids={[id]} open={sched} onOpenChange={setSched} onDone={() => { api.get(`/outbox/${id}`).then((r) => setItem(r.data)); onDone(true); }} />}
      </DialogContent>
    </Dialog>
  );
}

function DigestToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/outbox/digest/me").then((r) => setOn(r.data.enabled)); }, []);
  const set = async (v) => { setOn(v); await api.put("/outbox/digest/me", { enabled: v }); toast.success(v ? "Morning Outbox reminder on" : "Morning Outbox reminder off"); };
  if (on === null) return null;
  return <label className="flex items-center gap-2 text-xs text-slate-600 px-2" title="Weekdays 8am Irish time, only when emails are waiting"><Switch checked={on} onCheckedChange={set} data-testid="outbox-digest-toggle" />8am reminder email</label>;
}

function BookingEmailToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/alerts/booking-email/me").then((r) => setOn(r.data.enabled)); }, []);
  const set = async (v) => { setOn(v); await api.put("/alerts/booking-email/me", { enabled: v }); toast.success(v ? "Booking alert emails on" : "Booking alert emails off"); };
  if (on === null) return null;
  return <label className="flex items-center gap-2 text-xs text-slate-600 px-2" title="Email me as soon as one of my leads (or an unowned lead) books a consultation"><Switch checked={on} onCheckedChange={set} data-testid="booking-email-toggle" />Booking alert email</label>;
}

export default function Outbox() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("queued");
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const load = () => api.get("/outbox", { params: { status } }).then((r) => { setItems(r.data); setSel((s) => s.filter((id) => r.data.some((i) => i.id === id))); window.dispatchEvent(new Event("outbox-changed")); });
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const queued = status === "queued";
  const bulk = async (kind) => {
    if (kind === "send" && !window.confirm(`Send ${sel.length} email(s) now?`)) return;
    if (kind === "skip" && !window.confirm(`Remove ${sel.length} email(s) from the Outbox? Their campaign moves on to the next step.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/outbox/${kind}`, { ids: sel }); report(data, kind === "send" ? "sent" : "removed"); setSel([]); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div>
      <PageHeader eyebrow="Campaigns" title="Outbox">
        <DigestToggle />
        <BookingEmailToggle />
        <Select value={status} onValueChange={(v) => { setStatus(v); setSel([]); }}><SelectTrigger className="w-40" data-testid="outbox-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="queued">Queued</SelectItem><SelectItem value="sent">Sent</SelectItem><SelectItem value="skipped">Removed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent></Select>
        <Button variant="outline" onClick={load} data-testid="outbox-refresh-btn"><RefreshCw size={15} className="mr-1" />Refresh</Button>
      </PageHeader>
      <div className="p-6 lg:p-10 space-y-4">
        <p className="text-sm text-slate-500 max-w-3xl">Campaign emails wait here when their step is due. Nothing is sent until you click Send. The next step's delay starts counting when an email is sent.</p>
        {queued && sel.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 fade-up" data-testid="outbox-bulk-bar">
            <span className="text-sm font-semibold text-blue-900" data-testid="outbox-selected-count">{sel.length} selected</span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="bg-white border-red-300 text-red-700 hover:bg-red-50" disabled={busy} onClick={() => bulk("skip")} data-testid="outbox-bulk-remove-btn"><Trash2 size={14} className="mr-1" />Remove {sel.length}</Button>
              <Button size="sm" variant="outline" className="bg-white" disabled={busy} onClick={() => setSchedOpen(true)} data-testid="outbox-bulk-schedule-btn"><CalendarClock size={14} className="mr-1" />Schedule {sel.length}</Button>
              <Button size="sm" disabled={busy} onClick={() => bulk("send")} data-testid="outbox-bulk-send-btn"><Send size={14} className="mr-1" />{busy ? "Sending…" : `Send ${sel.length}`}</Button>
            </div>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="outbox-table">
            <thead><tr className="border-b bg-slate-50 text-left">
              <th className="p-3 w-10">{queued && <Checkbox checked={items.length > 0 && sel.length === items.length} onCheckedChange={(c) => setSel(c ? items.map((i) => i.id) : [])} data-testid="outbox-select-all" />}</th>
              {["Lead", "Subject", "Campaign", "Step", queued ? "Queued" : "Updated", ""].map((h, i) => <th key={i} className="p-3 label-xs font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b last:border-0 hover:bg-blue-50/40 transition-colors cursor-pointer" onClick={() => setOpen(i.id)} data-testid={`outbox-row-${i.id}`}>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>{queued && <Checkbox checked={sel.includes(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`outbox-select-${i.id}`} />}</td>
                  <td className="p-3"><Link to={`/leads/${i.lead_id}`} onClick={(e) => e.stopPropagation()} className="font-semibold hover:text-blue-600">{i.lead?.company_name || "—"}</Link><div className="text-xs text-slate-400">{fullName(i.lead)} · {i.lead?.email}</div></td>
                  <td className="p-3">{i.subject}{i.scheduled_at && queued && <div className="mt-1 inline-flex items-center gap-1 text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5" data-testid={`outbox-scheduled-${i.id}`}><CalendarClock size={11} />{fmtDublin(i.scheduled_at)}</div>}{i.error && <div className="text-xs text-red-600">Failed: {i.error}</div>}</td>
                  <td className="p-3 text-xs text-blue-700 font-medium">{i.campaign_name}</td>
                  <td className="p-3">{i.stage ? <StageBadge stage={i.stage} /> : `Step ${i.step_index + 1}`}</td>
                  <td className="p-3 text-xs text-slate-500">{ago(i.resolved_at || i.created_at)}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" data-testid={`outbox-open-${i.id}`}><Eye size={14} className="mr-1" />{queued ? "Review" : "View"}</Button></td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={7} className="p-10 text-center text-slate-500" data-testid="outbox-empty">{queued ? "Outbox is empty. Campaign emails appear here when a step is due." : "Nothing here yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <ScheduleDialog ids={sel} open={schedOpen} onOpenChange={setSchedOpen} onDone={() => { setSel([]); load(); }} />
      <OutboxDialog id={open} onClose={() => setOpen(null)} onDone={(reload) => reload !== false && load()} />
    </div>
  );
}
